import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useCallback } from 'react';
import styled from '@emotion/styled';
import { PaperAirplaneIcon, PaperClipIcon, XMarkIcon, FaceSmileIcon } from '@heroicons/react/24/outline';
const InputContainer = styled.div`
  border-top: 1px solid #e5e7eb;
  background-color: #ffffff;
  padding: 1rem;

  .dark & {
    border-color: #374151;
    background-color: #1f2937;
  }
`;
const ReplyContainer = styled.div`
  margin-bottom: 0.75rem;
  padding: 0.75rem;
  background-color: #f3f4f6;
  border-left: 3px solid #3b82f6;
  border-radius: 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .dark & {
    background-color: #374151;
  }
`;
const ReplyContent = styled.div`
  flex-grow: 1;
`;
const ReplyLabel = styled.div`
  font-size: 0.75rem;
  color: #3b82f6;
  font-weight: 500;
  margin-bottom: 0.25rem;
`;
const ReplyText = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  .dark & {
    color: #9ca3af;
  }
`;
const CancelReplyButton = styled.button`
  padding: 0.25rem;
  color: #6b7280;
  hover:color: #374151;
  transition: color 0.2s;

  &:hover {
    color: #374151;
  }

  .dark & {
    color: #9ca3af;
    
    &:hover {
      color: #d1d5db;
    }
  }
`;
const AttachedFilesContainer = styled.div`
  margin-bottom: 0.75rem;
`;
const FileTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;

  .dark & {
    background-color: #374151;
    border-color: #4b5563;
  }
`;
const InputArea = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
  background-color: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 1rem;
  padding: 0.75rem;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: #3b82f6;
  }

  .dark & {
    background-color: #374151;
    border-color: #4b5563;

    &:focus-within {
      border-color: #3b82f6;
    }
  }
`;
const MessageInput = styled.textarea`
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  resize: none;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #1f2937;
  min-height: 1.25rem;
  max-height: 6rem;

  &::placeholder {
    color: #9ca3af;
  }

  .dark & {
    color: #f3f4f6;
    
    &::placeholder {
      color: #6b7280;
    }
  }
`;
const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;

  ${(props)=>props.primary ? `
    background-color: #3b82f6;
    color: white;

    &:hover {
      background-color: #2563eb;
    }

    &:disabled {
      background-color: #9ca3af;
      cursor: not-allowed;
    }
  ` : `
    background-color: transparent;
    color: #6b7280;

    &:hover {
      background-color: #f3f4f6;
      color: #374151;
    }

    .dark & {
      color: #9ca3af;

      &:hover {
        background-color: #4b5563;
        color: #d1d5db;
      }
    }
  `}

  ${(props)=>props.disabled && `
    opacity: 0.5;
    cursor: not-allowed;
  `}
`;
const EmojiPicker = styled.div`
  position: absolute;
  bottom: 100%;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.5rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 10;
  display: flex;
  gap: 0.25rem;

  .dark & {
    background: #1f2937;
    border-color: #374151;
  }
`;
const MentionDropdown = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  margin-bottom: 0.5rem;
  max-height: 10rem;
  overflow-y: auto;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 10;

  .dark & {
    background: #1f2937;
    border-color: #374151;
  }
`;
const MentionItem = styled.div`
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f3f4f6;
  }

  .dark &:hover {
    background-color: #374151;
  }
`;
const UserChatInput = ({ onSendMessage, isLoading = false, placeholder = "메시지를 입력하세요...", replyTo, onCancelReply, onTyping, disabled = false })=>{
    const [message, setMessage] = useState('');
    const [attachedFiles, setAttachedFiles] = useState([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showMentionDropdown, setShowMentionDropdown] = useState(false);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const typingTimeoutRef = useRef();
    // Mock data for mentions
    const mockUsers = [
        {
            userId: 'john_doe',
            displayName: 'John Doe'
        },
        {
            userId: 'jane_doe',
            displayName: 'Jane Doe'
        }
    ];
    const handleSubmit = useCallback(()=>{
        if (!message.trim() || isLoading || disabled) return;
        onSendMessage(message.trim(), attachedFiles.length > 0 ? attachedFiles : [], replyTo || null);
        setMessage('');
        setAttachedFiles([]);
        if (onCancelReply) onCancelReply();
        // Reset textarea height
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    }, [
        message,
        attachedFiles,
        replyTo,
        isLoading,
        disabled,
        onSendMessage,
        onCancelReply
    ]);
    const handleKeyDown = (e)=>{
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
        }
    };
    const handleInputChange = (e)=>{
        const value = e.target.value;
        setMessage(value);
        // Auto-resize textarea
        const target = e.target;
        target.style.height = 'auto';
        target.style.height = `${Math.min(target.scrollHeight, 96)}px`;
        // Check for mention trigger
        if (value.endsWith('@')) {
            setShowMentionDropdown(true);
        } else if (!value.includes('@')) {
            setShowMentionDropdown(false);
        }
        // Handle typing indicator
        if (onTyping) {
            onTyping(value.length > 0);
            // Clear previous timeout
            if (typingTimeoutRef.current) {
                clearTimeout(typingTimeoutRef.current);
            }
            // Set new timeout to stop typing indicator
            typingTimeoutRef.current = setTimeout(()=>{
                onTyping(false);
            }, 2000);
        }
    };
    const handleFileAttach = ()=>{
        fileInputRef.current?.click();
    };
    const handleFileChange = (e)=>{
        const files = Array.from(e.target.files || []);
        setAttachedFiles((prev)=>[
                ...prev,
                ...files
            ]);
    };
    const removeFile = (index)=>{
        setAttachedFiles((prev)=>prev.filter((_, i)=>i !== index));
    };
    const canSend = message.trim().length > 0 && !isLoading && !disabled;
    const handleEmojiSelect = (emoji)=>{
        setMessage((prev)=>prev + emoji);
        setShowEmojiPicker(false);
        textareaRef.current?.focus();
    };
    const handleMentionSelect = (user)=>{
        const lastAtIndex = message.lastIndexOf('@');
        setMessage((prev)=>prev.slice(0, lastAtIndex) + `@${user.userId} `);
        setShowMentionDropdown(false);
        textareaRef.current?.focus();
    };
    const emojiList = [
        '😀',
        '😊',
        '😂',
        '❤️',
        '👍',
        '🎉'
    ];
    const handleDragOver = (e)=>{
        e.preventDefault();
        e.stopPropagation();
    };
    const handleDrop = (e)=>{
        e.preventDefault();
        e.stopPropagation();
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            setAttachedFiles((prev)=>[
                    ...prev,
                    ...files
                ]);
        }
    };
    return /*#__PURE__*/ _jsxs(InputContainer, {
        "data-testid": "people-chat-input",
        onDragOver: handleDragOver,
        onDrop: handleDrop,
        children: [
            /*#__PURE__*/ _jsx("input", {
                ref: fileInputRef,
                type: "file",
                hidden: true,
                multiple: true,
                accept: "image/*,video/*,.pdf,.txt,.doc,.docx",
                onChange: handleFileChange,
                "data-testid": "file-input"
            }),
            replyTo && /*#__PURE__*/ _jsxs(ReplyContainer, {
                id: "reply-context",
                children: [
                    /*#__PURE__*/ _jsxs(ReplyContent, {
                        children: [
                            /*#__PURE__*/ _jsxs(ReplyLabel, {
                                children: [
                                    replyTo.sender.displayName,
                                    "에게 답장"
                                ]
                            }),
                            /*#__PURE__*/ _jsx(ReplyText, {
                                children: replyTo.content
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CancelReplyButton, {
                        onClick: onCancelReply,
                        title: "답장 취소",
                        children: /*#__PURE__*/ _jsx(XMarkIcon, {
                            className: "w-4 h-4"
                        })
                    })
                ]
            }),
            attachedFiles.length > 0 && /*#__PURE__*/ _jsx(AttachedFilesContainer, {
                children: attachedFiles.map((file, index)=>/*#__PURE__*/ _jsxs(FileTag, {
                        children: [
                            /*#__PURE__*/ _jsx(PaperClipIcon, {
                                className: "w-4 h-4"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                children: file.name
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>removeFile(index),
                                title: "파일 제거",
                                children: /*#__PURE__*/ _jsx(XMarkIcon, {
                                    className: "w-4 h-4"
                                })
                            })
                        ]
                    }, index))
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    position: 'relative'
                },
                children: [
                    showMentionDropdown && /*#__PURE__*/ _jsx(MentionDropdown, {
                        "data-testid": "mention-dropdown",
                        children: mockUsers.map((user)=>/*#__PURE__*/ _jsx(MentionItem, {
                                onClick: ()=>handleMentionSelect(user),
                                children: user.displayName
                            }, user.userId))
                    }),
                    /*#__PURE__*/ _jsxs(InputArea, {
                        children: [
                            /*#__PURE__*/ _jsx(ActionButton, {
                                onClick: handleFileAttach,
                                title: "파일 첨부",
                                "aria-label": "파일 첨부",
                                disabled: disabled,
                                children: /*#__PURE__*/ _jsx(PaperClipIcon, {
                                    className: "w-5 h-5"
                                })
                            }),
                            /*#__PURE__*/ _jsx(MessageInput, {
                                ref: textareaRef,
                                value: message,
                                onChange: handleInputChange,
                                onKeyDown: handleKeyDown,
                                placeholder: placeholder,
                                rows: 1,
                                "data-testid": "people-chat-textarea",
                                "aria-label": "메시지 입력",
                                "aria-describedby": replyTo ? "reply-context" : undefined,
                                disabled: disabled
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                style: {
                                    position: 'relative'
                                },
                                children: [
                                    /*#__PURE__*/ _jsx(ActionButton, {
                                        onClick: ()=>setShowEmojiPicker(!showEmojiPicker),
                                        title: "이모지",
                                        disabled: disabled,
                                        children: /*#__PURE__*/ _jsx(FaceSmileIcon, {
                                            className: "w-5 h-5"
                                        })
                                    }),
                                    showEmojiPicker && /*#__PURE__*/ _jsx(EmojiPicker, {
                                        children: emojiList.map((emoji)=>/*#__PURE__*/ _jsx("button", {
                                                onClick: ()=>handleEmojiSelect(emoji),
                                                style: {
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '1.25rem'
                                                },
                                                children: emoji
                                            }, emoji))
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(ActionButton, {
                                primary: true,
                                disabled: !canSend,
                                onClick: handleSubmit,
                                title: "메시지 전송",
                                "data-testid": "people-chat-send-btn",
                                children: /*#__PURE__*/ _jsx(PaperAirplaneIcon, {
                                    className: "w-5 h-5"
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
export default UserChatInput;
